import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { Severity } from 'Data/ViewModels/Core/types';
import { format } from 'Lib/Format';
import coalesce from 'Lib/coalesce';
import toNumberOrNull from 'Lib/toNumberOrNull';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import getPlaceholderFromFormat from './getPlaceholderFromFormat';
import parseValue from './parseValue';

export type INumericInputProps = Pick<
  React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  'disabled' | 'id' | 'name' | 'onBlur' | 'onChange' | 'onKeyDown' | 'onFocus' | 'onBlur' | 'max' | 'min' | 'step'
> & {
  focusRef?: React.Ref<HTMLInputElement>;
  format?: any;
  severity?: Severity;
  value?: any;
  sfPrecision?: number;
  enterAsTab?: boolean;
};

/**
 * A numeric input that swaps between:
 * - A text input for displaying the formatted value
 * - A number input for entering the number value
 */
const NumericInputBase = ({
  disabled,
  format: formatSpecifier,
  id,
  focusRef,
  name,
  onChange,
  onKeyDown,
  max,
  min,
  severity,
  step = 'any',
  value,
  onFocus,
  onBlur,
  sfPrecision = 6,
  enterAsTab: supportEnterAsTab = false,
}: INumericInputProps) => {
  const [enteredValue, setEnteredValue] = useState<any>(value);
  const [dirty, setDirty] = useState<boolean>(false);
  const textInput = useRef<HTMLInputElement | null>(null);
  const numberInput = useRef<HTMLInputElement>(null);
  const cx = classNames('NumericInput', {
    [`Input-${severity}`]: !!severity,
  });

  // Sync value to display from props unless user has made local changes
  // Local changes flagged through handleChange and then synced through handleBlur
  if (value !== undefined && !dirty && enteredValue !== value) {
    setEnteredValue(value);
  }

  useEffect(() => {
    if (disabled) {
      if (numberInput.current) numberInput.current.style.display = 'none';
      if (textInput.current) textInput.current.style.display = '';
    }
  }, [disabled, numberInput, textInput]);

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (numberInput.current) numberInput.current.style.display = 'none';
    if (textInput.current) textInput.current.style.display = '';
    if (focusRef) (focusRef as any).current = textInput.current;

    //This bit is rather horrible.  If the value of the input has more than 6 sig figs,
    //parseValue will return undefined.  If you click into an input that already has a
    //stored value with more than 6 sig figs, and you don't change the value,
    //state.enteredValue will still be null.  We don't actually want to change the
    //value to null, so in this case we need to return the original value.
    //Seems pretty messed up -- tech debt logged: QP-2448
    var localValue = parseValue(event.target.value, sfPrecision);
    if (localValue === undefined) {
      if (enteredValue === null) {
        localValue = value;
      } else {
        localValue = enteredValue;
      }
    }
    setDirty(false);
    if (onChange) {
      (event as any).data = { name, value: localValue, submitValues: true };
      onChange(event as any);
    }
    onBlur?.(event);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const localValue = parseValue(event.target.value, sfPrecision);
    if (localValue !== undefined) {
      // Value held locally in component state so that half-entered values are not
      // put on state which is synced to the API avoiding these values being sent to the back-end
      // and affecting calculated values/warnings - QP-4483
      setEnteredValue(localValue);
      setDirty(true);
    }
  };

  const handleFocus = (_event: React.FocusEvent<HTMLInputElement>) => {
    if (textInput.current) textInput.current.style.display = 'none';
    if (numberInput.current) {
      numberInput.current.style.display = '';
      numberInput.current.focus();
      numberInput.current.select();
    }
    if (focusRef) (focusRef as any).current = numberInput.current;
    onFocus?.(_event);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.defaultPrevented && !supportEnterAsTab) {
      handleBlur(event as any);
    }
    onKeyDown?.(event);
  };

  const enteredVal = toNumberOrNull(coalesce(enteredValue));
  const displayedValue = coalesce(format(formatSpecifier)(enteredVal), '');

  return (
    <>
      {!disabled && (
        <input
          autoComplete="off"
          className={cx}
          max={max}
          min={min}
          name={name}
          onBlur={handleBlur}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={getPlaceholderFromFormat(formatSpecifier)}
          ref={numberInput}
          step={step}
          style={{ display: 'none' }}
          type="number"
          value={enteredVal === null ? '' : enteredVal}
        />
      )}
      <input
        className={cx}
        id={id}
        autoComplete="off"
        disabled={disabled}
        name={`${name}.Display`}
        onChange={() => {}}
        onFocus={(e) => {
          if (disabled) return;
          handleFocus(e);
        }}
        placeholder={getPlaceholderFromFormat(formatSpecifier)}
        ref={(input) => {
          textInput.current = input;
          if (focusRef) (focusRef as any).current = input;
        }}
        type="text"
        value={displayedValue}
      />
    </>
  );
};

const NumericInput = withInjectedProps(NumericInputBase);

export { NumericInput, NumericInputBase };
export default NumericInput;
